import { Loader2 } from "lucide-react";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  Unsubscribe,
  where,
} from "firebase/firestore";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { auth, db } from "../../firebase/config";
import { sendMemberVerificationEmail } from "../../utils/sendMemberVerificationEmail";
import { Store } from "../type/Store";
import connectionMonitor from "../../utils/connectionMonitor";
import masterDataCache from "../../utils/offlineStorage/masterDataCache";
import { logPOSEvent } from "../../utils/backgroundLogger";
import { clearAuthenticatorSession } from "../../utils/posAuthenticator";
import { applyColorMode } from "../../utils/colorMode";
import { currentWorkstation, listConnectedStores, resumeStationConnection, watchStationPresence, writeWorkstationConnection } from "../../utils/stationConnection";
import { clearAllowedWorkstationStores, readAllowedWorkstationStores, restrictWorkstationStores } from "../../utils/workstationStoreAccess";
import { isTauriMode } from "../../utils/environmentDetector";
import { clearOpenSessionMirrors, releaseStoreScope } from "../../utils/storeScope";
import { flushOfflineBillsOnLogout } from "../../utils/mevOfflineBatch";

interface AuthContextProps {
  isAuthStateChanged: boolean;
  currentUser: User;
  refresh: boolean;
  memberStores: Store[];
  adminStores: Store[];
  kdsStores: Store[];
  setMemberStores: () => void;
  setAdminStores: () => void;
  notifications: NotificationProps[];
  register: () => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendVerification: () => Promise<void>;
  rerender: () => void;
  updateStoreDetail: () => void;
  getCustomClaimRole: () => Promise<string>;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthStateChanged: false,
  currentUser: {} as User,
  refresh: false,
  memberStores: [],
  adminStores: [],
  kdsStores: [],
  setMemberStores: () => { },
  setAdminStores: () => { },
  notifications: [],
  register: async () => { },
  login: async () => { },
  logout: async () => { },
  forgotPassword: async () => { },
  signInWithGoogle: async () => { },
  sendVerification: async () => { },
  rerender: () => { },
  updateStoreDetail: () => { },
  getCustomClaimRole: async () => {
    return "";
  },
});

export const useAuth = () => useContext(AuthContext);

type Props = {
  children: ReactNode;
};

export type NotificationProps = {
  id: string;
  content?: LanguageProps;
  navigate?: string;
  time?: Timestamp;
  title?: LanguageProps;
  user?: string;
};

export type LanguageProps = {
  en?: string;
  ms?: string;
  cn?: string;
  hk?: string;
};

const AuthContextProvider = ({ children }: Props) => {
  const [isAuthStateChanged, setIsAuthStateChanged] = useState(false);
  const [memberStores, setMemberStores] = useState<Store[] | null>(null);
  const [adminStores, setAdminStores] = useState<Store[] | null>(null);
  const [kdsStores, setKdsStores] = useState<Store[] | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [refresh, toggleRefresh] = useState(false);
  const [notifications, setNotifications] = useState<DocumentData[] | null>(
    null
  );

  const loadWorkstationStore = useCallback(async (storeId: string) => {
    if (!storeId) return;
    let choices: Store[] = [];
    try {
      const rows = await listConnectedStores();
      choices = rows
        .filter((row) => row && row.id)
        .map((row) => ({ id: row.id, name: row.name || row.id } as Store));
    } catch (error) {
      console.warn("[AuthContext] Workstation stores were not listed:", error);
    }
    if (!choices.some((store) => store.id === storeId)) {
      try {
        const snap = await getDoc(doc(db, "stores", storeId));
        if (snap.exists()) {
          choices.unshift({ id: snap.id, name: snap.data().name, ...snap.data() } as Store);
        }
      } catch (error) {
        console.warn("[AuthContext] Workstation store read failed:", error);
      }
    }
    if (!choices.length) {
      const cached = await masterDataCache.get("stores", storeId);
      if (cached) choices = [cached as Store];
    }
    const allowed = readAllowedWorkstationStores();
    if (allowed.length) {
      const allowedSet = new Set(allowed);
      choices = choices.filter((store) => allowedSet.has(store.id));
    } else if (storeId) {
      choices = choices.filter((store) => store.id === storeId);
    }
    if (choices.length) {
      const selected = localStorage.getItem("selectedStore") || "";
      if (!selected) {
        localStorage.setItem("selectedStore", choices[0].id);
      }
    }
    if (choices.length) {
      setAdminStores(choices);
      setMemberStores([]);
      setKdsStores([]);
      for (const store of choices) {
        await masterDataCache.save("stores", store.id, store);
      }
    }
    try {
      if (!localStorage.getItem("selectedStore")) localStorage.setItem("selectedStore", storeId);
    } catch {
      // The open store stays as the operator left it.
    }
  }, []);

  const setRoles = useCallback(async () => {
    try {
      const workstation = await currentWorkstation();
      if (workstation.workstation) {
        await loadWorkstationStore(workstation.storeId);
        return;
      }
      const userEmail = auth.currentUser!.email;
      if (!userEmail) return;

      // OFFLINE-FIRST: Load stores from IndexedDB cache first (instant!)
      console.log('[AuthContext] Loading stores from IndexedDB cache...');
      const loadFromCache = async () => {
        try {
          const masterDataCache = (await import('../../utils/offlineStorage/masterDataCache')).default;
          const cachedStores = await masterDataCache.getAll('stores');

          if (cachedStores && cachedStores.length > 0) {
            // Filter cached stores by user role
            const cachedAdminStores = cachedStores.filter((store: any) =>
              store.admins && Array.isArray(store.admins) && store.admins.includes(userEmail)
            );
            const cachedMemberStores = cachedStores.filter((store: any) =>
              store.members && Array.isArray(store.members) && store.members.includes(userEmail)
            );
            const cachedKdsStores = cachedStores.filter((store: any) =>
              store.kdsUsers && Array.isArray(store.kdsUsers) && store.kdsUsers.includes(userEmail)
            );

            if (cachedAdminStores.length > 0) {
              const visibleAdmins = await restrictWorkstationStores(cachedAdminStores);
              setAdminStores(visibleAdmins);
              console.log(`[AuthContext] ✓ Loaded ${visibleAdmins.length} admin stores from cache`);
            }
            if (cachedMemberStores.length > 0) {
              setMemberStores(cachedMemberStores);
              console.log(`[AuthContext] ✓ Loaded ${cachedMemberStores.length} member stores from cache`);
            }
            if (cachedKdsStores.length > 0) {
              setKdsStores(cachedKdsStores);
            }

            return true; // Cache hit
          }
          return false; // Cache miss
        } catch (error) {
          console.warn('[AuthContext] Error loading from cache:', error);
          return false;
        }
      };

      // Load from cache first (non-blocking for UI)
      const cacheHit = await loadFromCache();

      // BACKGROUND SYNC: Fetch fresh data from Firestore (non-blocking)
      // Only if online - don't make network requests when offline
      const isOnline = connectionMonitor.getStatus();
      console.log(`Offline Debug [AuthContext] Connection status: ${isOnline ? 'Online' : 'Offline'}`);

      if (isOnline) {
        console.log('Offline Debug [AuthContext] Syncing stores from Firestore in background...');
        (async () => {
          try {
            const storesRef = collection(db, "stores");
            const queryContainAdmin = query(
              storesRef,
              where("admins", "array-contains", userEmail),
              limit(100)
            );

            const storeContainAdminDocs = await getDocs(queryContainAdmin);

            const adminStores_: Store[] = [];
            storeContainAdminDocs.docs.forEach((doc) => {
              const adminStore = { id: doc.id, name: doc.data().name, ...doc.data() };
              adminStores_.push(adminStore);
            });

            // Update state with fresh data
            const visibleAdmins = await restrictWorkstationStores(adminStores_);
            if (
              !adminStores ||
              !adminStores.every((store, index) => {
                return store.id === visibleAdmins[index]?.id;
              })
            ) {
              setAdminStores(visibleAdmins);
              console.log(`Offline Debug [AuthContext] ✓ Updated ${visibleAdmins.length} admin stores from Firestore`);
            }

            // Save to cache for next time
            const masterDataCache = (await import('../../utils/offlineStorage/masterDataCache')).default;
            for (const store of adminStores_) {
              await masterDataCache.save('stores', store.id, store);
            }
            console.log(`Offline Debug [AuthContext] ✓ Saved ${adminStores_.length} admin stores to cache`);

            const queryContainMember = query(
              storesRef,
              where("members", "array-contains", userEmail),
              limit(100)
            );

            const storeContainMemberDocs = await getDocs(queryContainMember);
            const memberStores_: Store[] = [];
            storeContainMemberDocs.docs.forEach((doc) => {
              const memberStore = {
                id: doc.id,
                name: doc.data().name,
                ...doc.data(),
              };
              memberStores_.push(memberStore);
            });

            // Update state with fresh data
            if (
              !memberStores ||
              !memberStores.every((store, index) => {
                return store.id === memberStores_[index].id;
              })
            ) {
              setMemberStores(memberStores_);
              console.log(`Offline Debug [AuthContext] ✓ Updated ${memberStores_.length} member stores from Firestore`);
            }

            // Save to cache for next time
            for (const store of memberStores_) {
              await masterDataCache.save('stores', store.id, store);
            }
            console.log(`Offline Debug [AuthContext] ✓ Saved ${memberStores_.length} member stores to cache`);

            const queryContainKds = query(
              storesRef,
              where("kdsUsers", "array-contains", userEmail),
              limit(100)
            );
            const storeContainKdsDocs = await getDocs(queryContainKds);
            const kdsStores_: Store[] = [];
            storeContainKdsDocs.docs.forEach((doc) => {
              kdsStores_.push({ id: doc.id, name: doc.data().name, ...doc.data() });
            });
            setKdsStores(kdsStores_);
            for (const store of kdsStores_) {
              await masterDataCache.save('stores', store.id, store);
            }
          } catch (error) {
            console.warn('Offline Debug [AuthContext] Background sync failed (non-critical):', error);
            // Don't throw - cache data is already loaded
          }
        })();
      } else {
        console.log('Offline Debug [AuthContext] Offline - using cached stores only');
        // Define cachedStores from method start or fetch explicitly if needed (it was likely intended to be available)
        // Since we are inside setRoles, let's fetch it if we don't have it, or use the one we likely fetched at the top of the function if it existed.
        // Looking at the code, it seems I need to fetch it here as it's not in scope.
        const cachedStores = await masterDataCache.getAll('stores');

        // CRITICAL FIX: Populate state from cache when offline!
        if (cachedStores.length > 0) {
          const cachedAdminStores = cachedStores.filter(store =>
            store.users && store.users[userEmail] && store.users[userEmail].role === 'admin' ||
            store.createdBy === userEmail ||
            (store.members && store.members.includes(userEmail) === false) // Assume admin if not member? Better logic needed based on data structure
          );
          // In offline mode, we might not have perfect role info if not stored explicitly. 
          // But usually 'adminStores' and 'memberStores' are separated in how we query them.
          // However, for the cache we just have 'stores'. 

          // Better approach: Rely on the 'stores' cache which contains ALL stores for this user.
          // We need to re-derive admin vs member. 

          // Let's look at how we saved them. We correctly identify them during online fetch.
          // But we save them all to 'stores' collection in cache.

          // SIMPLE FIX: Just set them all as adminStores for now if we can't distinguish, 
          // OR check the store data structure if it has 'role' or 'permissions'.
          // Looking at line 180, we match by ID.

          // Let's try to filter based on common patterns or just enable both for safety offline.
          // Actually, let's just look at the 'members' array in the store doc.

          const adminStores_ = cachedStores.filter(s => s.createdBy === userEmail || (s.users && s.users[userEmail]?.role === 'admin'));
          const memberStores_ = cachedStores.filter(s => s.members && s.members.includes(userEmail));
          const kdsStores_ = cachedStores.filter(s => s.kdsUsers && s.kdsUsers.includes(userEmail));

          // Fallback: If we can't determine, add to adminStores if createdBy matches
          setAdminStores(await restrictWorkstationStores(adminStores_));
          setMemberStores(memberStores_);
          setKdsStores(kdsStores_);

          console.log(`Offline Debug [AuthContext] Restored ${adminStores_.length} admin and ${memberStores_.length} member stores from cache`);
        }
      }
    } catch (e) {
      console.error('[AuthContext] Error in setRoles:', e);
    }
  }, [adminStores, memberStores, loadWorkstationStore]);

  function register(email: string, password: string) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  async function login(email: string, password: string) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    releaseStoreScope();
    try {
      localStorage.removeItem("selectedStore");
      localStorage.removeItem("store-id");
    } catch {
      // The next store selection writes these again.
    }
    await logPOSEvent("LOGIN", { user: email, timestamp: Date.now() });
    return result;
  }

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  function sendVerification() {
    if (auth.currentUser) {
      return sendMemberVerificationEmail();
    }
  }

  function forgotPassword(email: string) {
    return sendPasswordResetEmail(auth, email, {
      url: window.location.protocol + "//" + window.location.host + "/login",
    });
  }

  function rerender() {
    toggleRefresh(!refresh);
  }

  function updateStoreDetail(store: Store) {
    const memberStore = memberStores?.find(
      (memberStore) => memberStore.id === store.id
    );
    if (memberStore) {
      Object.assign(memberStore, store);
      rerender();
    }

    const adminStore = adminStores?.find(
      (adminStore) => adminStore.id === store.id
    );
    if (adminStore) {
      Object.assign(adminStore, store);
      rerender();
    }
  }

  async function getCustomClaimRole() {
    await auth.currentUser?.getIdToken(true);
    const decodedToken = await auth.currentUser?.getIdTokenResult();
    return decodedToken?.claims.stripeRole;
  }

  async function logout() {
    await flushOfflineBillsOnLogout();
    window.dispatchEvent(new Event("pos-user-logout"));
    window.dispatchEvent(new Event("back-office-logout"));
    const email = auth.currentUser?.email;
    if (email) {
      await logPOSEvent("LOGOUT", { user: email, timestamp: Date.now() });
    }
    setAdminStores(null);
    setMemberStores(null);
    setKdsStores(null);
    setNotifications(null);
    releaseStoreScope();
    clearOpenSessionMirrors();
    applyColorMode("light", window.location.hash.replace(/^#/, ""));
    clearAuthenticatorSession();
    try {
      localStorage.removeItem("selectedStore");
      localStorage.removeItem("store-id");
    } catch { /* The next store selection writes these again. */ }
    clearAllowedWorkstationStores();
    writeWorkstationConnection(false);
    const result = await signOut(auth);
    return result;
  }

  const value: any = {
    isAuthStateChanged,
    currentUser,
    refresh,
    adminStores,
    memberStores,
    kdsStores,
    setKdsStores,
    setMemberStores,
    setAdminStores,
    notifications,
    register,
    login,
    logout,
    forgotPassword,
    signInWithGoogle,
    sendVerification,
    rerender,
    updateStoreDetail,
    getCustomClaimRole,
  };

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;
    (async () => {
      try {
        const restored = await resumeStationConnection();
        const selected = localStorage.getItem("selectedStore") || "";
        if (isTauriMode() && restored === "none" && selected && auth.currentUser && !auth.currentUser.isAnonymous) {
          await signOut(auth);
        }
        watchStationPresence();
      } catch (error) {
        console.warn("[AuthContext] Workstation connection was not restored:", error);
      }
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setIsAuthStateChanged(true);
        if (user && !user.isAnonymous) {
          setRoles();
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let unsubscribeNotifications: Unsubscribe | null = null;
    if (auth.currentUser?.email) {
      // CRITICAL: Only subscribe when online - prevents network requests when offline
      const isOnline = connectionMonitor.getStatus();
      if (!isOnline) {
        console.log('Offline Debug [AuthContext] Skipping notifications listener - offline');
        return;
      }

      const weekAgo = new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000);

      const qNotifications = query(
        collection(db, "notifications"),
        where("user", "==", auth.currentUser.email),
        where("time", ">", weekAgo),
        orderBy("time", "desc"),
        limit(5)
      );
      unsubscribeNotifications = onSnapshot(
        qNotifications,
        (querySnapshot) => {
          const notifications: DocumentData[] = [];
          querySnapshot.forEach((doc) => {
            const notification = doc.data();
            notification.id = doc.id;
            notifications.push(notification);
          });
          // don't need to check metatdata.hasPendingWrites since user wont write to notifications direcly.
          setNotifications(notifications);
        },
        (e) => {
          console.warn('[AuthContext] Error in notifications listener:', e);
        }
      );
    }

    return () => {
      unsubscribeNotifications && unsubscribeNotifications();
    };
  }, [auth.currentUser]);

  return (
    <AuthContext.Provider value={value}>
      {/* avoid weird redirecting caused by (onAuthStateChanged run after routes) */}
      {isAuthStateChanged && children}
      {!isAuthStateChanged && (
        <div className="flex h-screen w-full items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-orange-300" />
        </div>
      )}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
